import React, {
    useContext,
    useState,
    useEffect,
    useRef
} from 'react';
import { useNavigate, Link } from "react-router";
import { useQuery, useMutation } from '@tanstack/react-query';

import { getCustomer, NotificationContext, handleError } from '../router'

import * as QUERIES from "../query-functions";

import LoadingIndicator from '../components/loading-indicator';
import LogoutButton from '../components/logout-button';

import SpektrixSession from '../../scripts/components/SpektrixSession';

let NewCustomer = null;

const CreateAccount = () => {

    const {notification, setNotification} = useContext(NotificationContext);

    let navigate = useNavigate();

    const { data: customer, isPending: customerPending, isFetching: customerFetching } = getCustomer();

    const [validationMessages, setValidationMessages] = useState(false);
    const [addressList, setAddressList] = useState(null);
    const [fullAddressID, setFullAddressID] = useState(null);
    const [showAddressFields, setShowAddressFields] = useState(false);
    const [duplicateEntry, setDuplicateEntry] = useState(false);
    const [customerData, setCustomerData] = useState({
        fName: "",
        sName: "",
        email: "",
        confirmEmail: "",
        password: "",
        confirmPassword: "",
        mobile: "",
        house: "",
        line1: "",
        town: "",
        county: "",
        administrativeDivision: "",
        country: "GB",
        postcode: "",
        statements: []
    });

    const emailInputRef = useRef(null);

    const {
        isFetching: addressesFetching,
        isError: isAddressesError,
        data: addresses,
        error: addressesError,
        refetch: refetchAddresses
    } = useQuery({
        queryKey: ['addressesData'],
        queryFn: () => QUERIES.getAddressesFromPostcode(customerData.postcode),
        refetchOnWindowFocus: false,
        enabled: false, // disable until fetched
        retry: false
    })

    const {
        isFetching: fullAddressFetching,
        isError: isFullAddressError,
        data: fullAddress,
        error: fullAddressError,
        refetch: refetchFullAddress
    } = useQuery({
        queryKey: ['fullAddressData'],
        queryFn: () => QUERIES.getAddressFromPostcodeId(fullAddressID),
        refetchOnWindowFocus: false,
        enabled: false, // disable until fetched
        retry: false
    })

    const {
        isFetching: countriesFetching,
        data: countries,
        error: countriesError,
    } = useQuery({
        queryKey: ['countriesData'],
        queryFn: () => QUERIES.getCountries(),
        refetchOnWindowFocus: false,
        staleTime: 86400000
    })

    // Contact preferences are called statements in Spektrix
    const {
        isFetching: statementsFetching,
        data: statements,
        error: statementsError,
    } = useQuery({
        queryKey: ['statementsData'],
        queryFn: () => QUERIES.getStatements(),
        refetchOnWindowFocus: false,
        staleTime: 86400000
    })

    const createCustomer = useMutation({
        mutationFn: () => QUERIES.createCustomer(NewCustomer),
        onError: (error) => {
            if (error.message.toLowerCase().includes("duplicate")) {
                setDuplicateEntry(true);
            } else {
                handleError(error.message, setNotification);
            }
        },
        onSuccess: async (data) => {
            const session = new SpektrixSession();
            await session.updateSession(data.email, false, false);

            navigate(`/account`);
        },
    })

    const handleCheckboxChange = (e) => {
        const { value, checked } = e.target;
        
        setCustomerData(prevData => {
            let updatedStatements = [...prevData.statements];

            if (checked) {
                // add value if checked
                updatedStatements.push(value);
            } else {
                // remove value if unchecked
                updatedStatements = updatedStatements.filter(v => v !== value);
            }

            return { ...prevData, statements: updatedStatements };
        });
    };

    const validateFields = (data) => {
        if (!data) return;

        const messages = {};

        // Spektrix criteria
        if (data.email.length > 255 || data.confirmEmail.length > 255) messages.emails = "Email address must not be longer than 255 characters.";
        if (emailInputRef && emailInputRef.current && !emailInputRef.current.validity.valid) messages.emails = "Please provide a valid email address.";
        if (data.fName && data.fName.length > 30) messages.fName = "First name should not be longer than 30 characters.";
        if (data.sName && data.sName.length > 80) messages.sName = "Surname should not be longer than 80 characters.";

        // If empty
        if (!data.fName) messages.fName = "Please provide a First Name.";
        if (!data.sName) messages.sName = "Please provide a Surname.";
        if (data.email != data.confirmEmail) messages.emails = "Email addresses do not match.";
        if (!data.email || !data.confirmEmail) messages.emails = "Please provide matching email addresses.";
        if (data.password != data.confirmPassword) messages.passwords = "Passwords do not match.";
        if (!data.password || !data.confirmPassword) messages.passwords = "Please provide matching passwords.";
        if (!data.mobile) messages.mobile = "Please provide valid mobile number.";
        if (!data.line1) messages.line1 = "Please provide the first line of your address.";
        if (!data.town) messages.town = "Please provide a town.";
        if (data.country === 'US') {
            if (!data.administrativeDivision) messages.state = "Please provide a state.";
        } else {
            if (!data.county) messages.state = "Please provide a county.";
        }
        if (!data.postcode) messages.postcode = "Please provide a postcode.";

        return messages;
    }

    const submitCustomerData = (e) => {
        if (!e) return;
        e.preventDefault();
        setShowAddressFields(true);

        const messages = validateFields(customerData);
        setValidationMessages(messages);

        const has_messages = Object.keys(messages).length != 0;
        const all_messages_null = Object.values(messages).every(o => o === null);
        if (has_messages || !all_messages_null) return;

        // This is the customer in the spektrix format
        NewCustomer = {
            FirstName: customerData.fName,
            LastName: customerData.sName,
            Email: customerData.email,
            Password: customerData.password,
            Mobile: customerData.mobile,
            Addresses: [{
                Name: `${customerData.fName} ${customerData.sName}`,
                IsDelivery: true,
                IsBilling: true,
                Line1: customerData.line1,
                Town: customerData.town,
                AdministrativeDivision: customerData.country === 'US' ? customerData.administrativeDivision : customerData.county,
                Country: customerData.country,
                Postcode: customerData.postcode
            }],
            AgreedStatements: customerData.statements
        };
        createCustomer.mutateAsync();
    }

    useEffect(() => {

        if(!addresses || !addresses.length) return;

        if (customerData.house) {

            // Check for house number
            const matchingAddresses = addresses.filter((address) => address.description.startsWith(customerData.house));

            if (!matchingAddresses || !matchingAddresses.length) {
                // If no addresses match house number return all addresses and exit
                setAddressList(addresses);
                return;
            }

            if (matchingAddresses.length > 1) {
                // If more than one address matches house number return only matching addresses
                setAddressList(matchingAddresses);
            } else {
                // If only one address matches house number get the full address
                setAddressList(null);
                setFullAddressID(matchingAddresses[0].id);
            }

        } else {

            // If no house number return all addresses
            setAddressList(addresses);

        }
    }, [addresses]);

    useEffect(() => {
        if(!fullAddressID) return;
        refetchFullAddress();
    }, [fullAddressID]);

    useEffect(() => {
        if(!fullAddress) return;
        setAddressList(null);

        setCustomerData({
            ...customerData,
            line1: fullAddress.line1,
            town: fullAddress.town,
            county: fullAddress.country.isoCode === 'US' ? '' : fullAddress.administrativeDivision?.name,
            administrativeDivision: fullAddress.country.isoCode === 'US'
                ? (fullAddress.administrativeDivision?.isoCode || fullAddress.administrativeDivision?.name)
                : '',
            country: fullAddress.country.isoCode,
            postcode: fullAddress.postcode
        })

    }, [fullAddress]);

    useEffect(() => {
        if(!customerData) return;

        // If address fields have value hide button
        if (
            customerData &&
            customerData.line1 ||
            customerData.town ||
            customerData.county
        ) {
            setShowAddressFields(true);
        }

    }, [customerData]);

    if (customerPending || customerPending) return <LoadingIndicator type="page" />

    return (
        <form onSubmit={submitCustomerData} autoComplete="off" className='booking-pathway__container booking-pathway__create-account'>
                <div className='booking-pathway__main-content'>
                    <h1 className='booking-pathway__header'>Create account</h1>

                    {customer ?
                        <>
                            <p>You must be logged out to create a new account.</p>
                            <LogoutButton />
                        </>
                    :
                        <>
                            <div className="create-account-form">
                                <div className='form-field--group form-field--group__name'>
                                    <div className={`form-field form-field__first-name ${validationMessages.fName && 'form-field--error'}`}>
                                        <label htmlFor="fName">First Name
                                            <span className="label__required">(required)</span>
                                        </label>
                                        <input type="text"
                                                id="fName"
                                                value={customerData.fName}
                                                onChange={(e) => {
                                                    setCustomerData({...customerData, fName: e.target.value});
                                                    setValidationMessages({...validationMessages, fName: null});
                                                }}
                                                required />
                                        {validationMessages.fName &&
                                            <p className='validation-message'>{validationMessages.fName}</p>
                                        }
                                    </div>

                                    <div className={`form-field form-field__first-field__surname ${validationMessages.sName && 'form-field--error'}`}>
                                        <label htmlFor="sName">Surname
                                            <span className="label__required">(required)</span>
                                        </label>
                                        <input type="text"
                                                id="sName"
                                                value={customerData.sName}
                                                onChange={(e) => {
                                                    setCustomerData({...customerData, sName: e.target.value});
                                                    setValidationMessages({...validationMessages, sName: null});
                                                }}
                                                required />
                                        {validationMessages.sName &&
                                            <p className='validation-message'>{validationMessages.sName}</p>
                                        }
                                    </div>
                                </div>

                                <div className='form-field--group form-field--group__email'>
                                    <div className={`form-field form-field__email ${validationMessages.emails && 'form-field--error'}`}>
                                        <label htmlFor="email">Email address
                                            <span className="label__required">(required)</span>
                                        </label>
                                        <input type="email"
                                                id="email"
                                                value={customerData.email}
                                                onChange={(e) => {
                                                    setCustomerData({...customerData, email: e.target.value});
                                                    setValidationMessages({...validationMessages, emails: null});
                                                }}
                                                ref={emailInputRef}
                                                required />
                                        {validationMessages.emails &&
                                            <p className='validation-message'>{validationMessages.emails}</p>
                                        }
                                    </div>

                                    <div className={`form-field form-field__confirm-email ${validationMessages.emails && 'form-field--error'}`}>
                                        <label htmlFor="emailConfirm">Confirm email address
                                            <span className="label__required">(required)</span>
                                        </label>
                                        <input type="email"
                                                id="emailConfirm"
                                                value={customerData.confirmEmail}
                                                onChange={(e) => {
                                                    setCustomerData({...customerData, confirmEmail: e.target.value});
                                                    setValidationMessages({...validationMessages, emails: null});
                                                }}
                                                required />
                                        {validationMessages.emails &&
                                            <p className='validation-message'>{validationMessages.emails}</p>
                                        }
                                    </div>
                                </div>

                                <div className='form-field--group form-field--group__password'>
                                    <div className={`form-field form-field__password ${validationMessages.passwords && 'form-field--error'}`}>
                                        <label htmlFor="password">Password
                                            <span className="label__required">(required)</span>
                                        </label>
                                        <input type="password"
                                                id="password"
                                                value={customerData.password}
                                                onChange={(e) => {
                                                    setCustomerData({...customerData, password: e.target.value});
                                                    setValidationMessages({...validationMessages, passwords: null});
                                                }}
                                                autoComplete="new-password"
                                                required />
                                        {validationMessages.passwords &&
                                            <p className='validation-message'>{validationMessages.passwords}</p>
                                        }
                                    </div>
                                    <div className={`form-field form-field__confirm-password ${validationMessages.passwords && 'form-field--error'}`}>
                                        <label htmlFor="passwordConfirm">Confirm password
                                            <span className="label__required">(required)</span>
                                        </label>
                                        <input type="password"
                                                id="passwordConfirm"
                                                value={customerData.confirmPassword}
                                                onChange={(e) => {
                                                    setCustomerData({...customerData, confirmPassword: e.target.value});
                                                    setValidationMessages({...validationMessages, passwords: null});
                                                }}
                                                autoComplete="new-password"
                                                required />
                                        {validationMessages.passwords &&
                                            <p className='validation-message'>{validationMessages.passwords}</p>
                                        }
                                    </div>
                                </div>

                                <div className='form-field--group form-field--group__phone'>
                                    <div className={`form-field form-field__mobile ${validationMessages.mobile && 'form-field--error'}`}>
                                        <label htmlFor="mobile">Mobile
                                            <span className="label__required">(required)</span>
                                        </label>
                                        <input type="tel"
                                                id="mobile"
                                                value={customerData.mobile}
                                                onChange={(e) => {
                                                    setCustomerData({...customerData, mobile: e.target.value});
                                                    setValidationMessages({...validationMessages, mobile: null});
                                                }}
                                                required />
                                        {validationMessages.mobile &&
                                            <p className='validation-message'>{validationMessages.mobile}</p>
                                        }
                                    </div>
                                </div>

                                <div role="region" aria-live="polite" className='form-field--group form-field--group__address'>
                                    {(!showAddressFields && !customerData?.line1) &&
                                        <div className="form-field form-field__house-no">
                                            <label htmlFor="houseNo">House no.</label>
                                            <input type="text" id="houseNo" value={customerData.house} onChange={(e) => setCustomerData({...customerData, house: e.target.value})} />
                                        </div>
                                    }

                                    {(showAddressFields || customerData?.line1) &&
                                        <div className={`form-field form-field__address--line1 ${validationMessages.line1 && 'form-field--error'}`}>
                                            <label htmlFor="addressLine1">Address Line 1
                                                <span className="label__required">(required)</span>
                                            </label>
                                            <input type="text"
                                                    value={customerData.line1}
                                                    onChange={(e) => {
                                                        setCustomerData({...customerData, line1: e.target.value});
                                                        setValidationMessages({...validationMessages, line1: null});
                                                    }}
                                                    id="addressLine1"
                                                    required />
                                            {validationMessages.line1 &&
                                                <p className='validation-message'>{validationMessages.line1}</p>
                                            }
                                        </div>
                                    }
                                    {(showAddressFields || customerData?.town) &&
                                        <div className={`form-field form-field__address--town ${validationMessages.town && 'form-field--error'}`}>
                                            <label htmlFor="addressTown">Town
                                                <span className="label__required">(required)</span>
                                            </label>
                                            <input type="text"
                                                    value={customerData.town}
                                                    onChange={(e) => {
                                                        setCustomerData({...customerData, town: e.target.value});
                                                        setValidationMessages({...validationMessages, town: null});
                                                    }}
                                                    id="addressTown"
                                                    required />
                                            {validationMessages.town &&
                                                <p className='validation-message'>{validationMessages.town}</p>
                                            }
                                        </div>
                                    }
                                    {(showAddressFields || customerData?.administrativeDivision) &&
                                        <div className={`form-field form-field__address--state ${validationMessages.state ? 'form-field--error' : ''}`}>
                                            <label htmlFor="addressCountyState">
                                                {customerData?.country === 'US' ? "State" : "County"}
                                                <span className="label__required">(required)</span>
                                            </label>
                                            {customerData?.country === 'US' ? (
                                                <select name="states"
                                                        id="addressCountyState"
                                                        value={customerData.administrativeDivision}
                                                        onChange={(e) => setCustomerData({...customerData, administrativeDivision: e.target.value})}>
                                                        <option value="">Select State</option>
                                                        {countries?.find((c) => c.name === "United States")?.administrativeDivisions?.map((division) => (
                                                            <option key={division.isoCode} value={division.isoCode}>{division.name}</option>
                                                        ))}
                                                </select>
                                            ) : (
                                                <input type="text" value={customerData.county} onChange={(e) => setCustomerData({...customerData, county: e.target.value})} id="addressCountyState" />
                                            )}
                                        </div>
                                    }
                                    {(showAddressFields) &&
                                        <div className="form-field form-field__address--country">
                                            <label htmlFor="countriesSelect">Country
                                                <span className="label__required">(required)</span>
                                            </label>
                                            {countriesFetching ?
                                                <LoadingIndicator />
                                            : countriesError ?
                                                <p className='error-message'>{`There has been an error. ${countriesError.message}`}</p>
                                            :
                                                <select name="countries"
                                                        defaultValue={customerData.country}
                                                        id="countriesSelect"
                                                        onChange={(e) => setCustomerData({
                                                        ...customerData,
                                                        country: e.target.value,
                                                        // clear whichever field isn't relevant any more
                                                        county: '',
                                                        administrativeDivision: ''
                                                    })}>
                                                    {countries?.map((country) => (
                                                        <option key={country.isoCode} value={country.isoCode}>{country.name}</option>
                                                    ))}
                                                </select>
                                            }
                                        </div>
                                    }
                                    <div className={`form-field form-field__postcode ${validationMessages.postcode && 'form-field--error'}`}>
                                        <label htmlFor="postcode">
                                            {customerData?.country === 'US' ? "Zipcode" : "Postcode"}
                                            <span className="label__required">(required)</span>
                                        </label>
                                        <input type="text"
                                            id="postcode"
                                            value={customerData.postcode}
                                            onChange={(e) => {
                                                setCustomerData({...customerData, postcode: e.target.value});
                                                setValidationMessages({...validationMessages, postcode: null});
                                            }}
                                            required />
                                        {validationMessages.postcode &&
                                            <p className='validation-message'>{validationMessages.postcode}</p>
                                        }
                                    </div>

                                    {!showAddressFields &&
                                        <div className="form-field--button">
                                            {(customerData.postcode && customerData.postcode.length) ?
                                                <button type="button"
                                                        className='btn'
                                                        onClick={() => refetchAddresses()}
                                                        onKeyDown={(e) => {if(e.key === "Enter") {e.preventDefault(); refetchAddresses()}}}
                                                        disabled={(addressesFetching || fullAddressFetching)}>
                                                    Find address
                                                    {(addressesFetching || fullAddressFetching) &&
                                                        <LoadingIndicator />
                                                    }
                                                </button>
                                            :
                                                <p className='btn disabled'><span className="sr-text">Add a postcode to </span>Find Address</p>
                                            }

                                            {(addressList && addressList.length && !addressesFetching) &&
                                                <>
                                                    <label htmlFor="addressesSelect" className='sr-text'>Choose address</label>
                                                    <select name="addresses" defaultValue="default" id="addressesSelect" onChange={(e) => setFullAddressID(e.target.value)}>
                                                        <option value="default" disabled="disabled">Choose address</option>
                                                        {addressList?.map((address) => (
                                                            <option key={address.id} value={address.id}>{address.description}</option>
                                                        ))}
                                                    </select>
                                                </>
                                            }
                                        </div>
                                    }

                                    {(isAddressesError && addressesError && !addressesFetching) &&
                                        <p className='error-message'>{addressesError.message}</p>
                                    }

                                    {(isFullAddressError && fullAddressError && !fullAddressFetching) &&
                                        <p className='error-message'>{fullAddressError.message}</p>
                                    }

                                    {!showAddressFields &&
                                        <div className="form-field--button form-field--button__show-address-fields">
                                            <button type="button"
                                                    className='btn'
                                                    onClick={() => setShowAddressFields(true)}
                                                    onKeyDown={(e) => {if(e.key === "Enter") {e.preventDefault(); setShowAddressFields(true)}}}>
                                                Add address manually
                                            </button>
                                        </div>
                                    }
                                </div>

                                {!statementsError &&
                                    <section className="section contact-preferences">
                                        <h2 className="booking-pathway__header">Contact Preferences</h2>
                                        <p className='booking-pathway__introduction'>I'd love to hear from Capital Theatres about shows, events, products and schemes, including memberships.</p>
                                        
                                        {statementsFetching ?
                                            <LoadingIndicator />
                                        : statements.map((statement) => (
                                            <div key={statement.id} className="form-field--checkbox">
                                            <input
                                                type="checkbox"
                                                name="statement"
                                                id={statement.id}
                                                value={statement.id}
                                                checked={customerData.statements.includes(statement.id)}
                                                onChange={handleCheckboxChange}
                                            />
                                                <label htmlFor={statement.id}>By {statement.text}</label>
                                            </div>
                                        ))
                                        }
                                    </section>
                                }
                            </div>

                            <div className='booking-pathway__footer'>
                                <ContinueButton createCustomer={createCustomer} />
                            </div>

                            {duplicateEntry &&
                                <p role="alert" className='error-message'>This email address already exists in our system. Please <Link to="/login" >go to login</Link>.</p>
                            }
                        </>
                    }

                </div>

                <div className='booking-pathway__footer'>

                    {!customer &&
                        <ContinueButton createCustomer={createCustomer} />
                    }
                </div>
        </form>
    );
};

const ContinueButton = (props) => {
    return (
        <button type="submit"
            className="btn btn--primary"
            disabled={props.createCustomer.isPending}>
                Continue
                {props.createCustomer.isPending &&
                    <LoadingIndicator />
                }
        </button>
    )
}

export default CreateAccount;
